import { Link } from "react-router-dom";
import { ArrowRight, Check, Zap, Brain, Apple, Dumbbell, Mail, MessagesSquare } from "lucide-react";

const INCLUDED = [
  { icon: Brain, label: "AI Workout Plans" },
  { icon: MessagesSquare, label: "FORGE-AI Coach Chat" },
  { icon: Apple, label: "Macro Tracker + Snap-to-Log" },
  { icon: Dumbbell, label: "Workout Logging w/ Rest Timer" },
  { icon: Mail, label: "Weekly Newsletter" },
  { icon: Zap, label: "PR Detection & History" },
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-black text-white">
      {/* TAGLINE BAND */}
      <section className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center gap-3">
          <div className="h-px w-10 bg-[#FF3333]" />
          <span className="font-mono text-[10px] tracking-[0.4em] text-white/60" data-testid="landing-tagline">
            MEMBERSHIP <span className="text-[#FF3333]">•</span> ONE PRICE <span className="text-[#FF3333]">•</span> EVERYTHING UNLOCKED
          </span>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* faint kanji watermark */}
        <div className="hidden md:block absolute -top-10 right-6 font-jp font-black text-[16rem] leading-none text-white/[0.025] select-none pointer-events-none">
          力
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-32 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <h1 className="font-heading uppercase tracking-tighter leading-[0.82]">
              <span
                className="block text-6xl sm:text-8xl md:text-[9.5rem] text-white"
                data-testid="hero-headline-line-1"
              >
                The Full System,
              </span>
              <span
                className="block mt-2 text-6xl sm:text-8xl md:text-[10rem] text-stroke-red grunge-text"
                data-testid="hero-headline-line-2"
              >
                On Tap.
              </span>
            </h1>

            <p
              className="mt-10 font-sub text-lg md:text-xl text-white/65 max-w-xl leading-snug"
              data-testid="hero-subcopy"
            >
              AI workout plans, FORGE-AI coach chat, macro tracker, workout logging, weekly
              newsletter, and more &mdash; all included in a single $9.99/month membership.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/pricing"
                data-testid="hero-cta-see-plans"
                className="inline-flex items-center gap-3 px-10 py-4 border border-[#FF3333] bg-black text-[#FF3333] font-heading uppercase tracking-[0.3em] text-sm hover:bg-[#FF3333] hover:text-black transition-colors duration-200"
                style={{ boxShadow: "0 0 24px rgba(255,51,51,0.18)" }}
              >
                Join Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/auth"
                data-testid="hero-login"
                className="font-mono text-[11px] tracking-widest text-white/60 hover:text-[#FF3333] transition-colors"
              >
                OR <span className="text-[#FF3333] underline">LOG IN</span>
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[11px] tracking-widest text-white/40">
              <div><span className="text-white">CANCEL</span> ANYTIME</div>
              <div><span className="text-white">NO</span> CONTRACTS</div>
              <div><span className="text-white">SECURE</span> BY STRIPE</div>
            </div>
          </div>

          {/* PRICING CARD */}
          <div className="md:col-span-5">
            <Link
              to="/pricing"
              data-testid="hero-pricing-card"
              className="block relative border border-[#FF3333]/40 bg-black hover:border-[#FF3333] transition-colors duration-300"
              style={{ boxShadow: "0 0 30px rgba(255,51,51,0.12)" }}
            >
              <div className="absolute top-0 left-0 h-px w-10 bg-[#FF3333]" />
              <div className="absolute top-0 right-0 h-px w-10 bg-[#FF3333]" />
              <div className="absolute bottom-0 left-0 h-px w-10 bg-[#FF3333]" />
              <div className="absolute bottom-0 right-0 h-px w-10 bg-[#FF3333]" />

              <div className="p-10 md:p-12">
                <div className="font-mono text-[11px] tracking-[0.5em] text-white/50">MONTHLY</div>
                <div className="mt-6 flex items-start gap-2">
                  <span className="font-heading text-[#FF3333] text-3xl md:text-4xl mt-3">$</span>
                  <span
                    className="font-heading text-white text-[7rem] md:text-[9rem] leading-[0.85]"
                    data-testid="hero-pricing-amount"
                  >
                    9.99
                  </span>
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.4em] text-white/40">
                  PER MONTH · CANCEL ANYTIME
                </div>

                <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between">
                  <span className="font-heading uppercase tracking-[0.3em] text-xs text-white/70">
                    Or 3-Month / Yearly
                  </span>
                  <span className="font-mono text-[10px] tracking-widest text-[#FF3333]">SAVE UP TO 33%</span>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* divider */}
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="h-px bg-gradient-to-r from-transparent via-[#FF3333]/30 to-transparent" />
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="included-section">
        <div className="grid md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF3333]" />
              <span className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">ONE MEMBERSHIP</span>
            </div>
            <h2 className="font-heading uppercase text-5xl md:text-6xl leading-[0.9]">
              Every tool. <br />
              <span className="text-stroke-red grunge-text">Zero upsells.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="font-sub text-white/65 text-lg leading-relaxed max-w-xl">
              FORGE-AI is more than a workout app &mdash; it&apos;s an all-in-one AI-powered fitness operating
              system. Intelligent programming, nutrition guidance, coaching, progress tracking, and
              accountability. One seamless membership.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {INCLUDED.map((f, i) => (
            <div
              key={f.label}
              className="bg-black p-7 md:p-9 hover:bg-[#0a0a0a] transition-colors group"
              data-testid={`included-feature-${i}`}
            >
              <f.icon className="w-5 h-5 text-[#FF3333]" />
              <div className="mt-6 font-heading uppercase tracking-wide text-xl md:text-2xl text-white leading-tight">
                {f.label}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-widest text-white/30 group-hover:text-white/60 transition-colors">
                <Check className="w-3 h-3 text-[#FF3333]" /> INCLUDED
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FORGE-AI PITCH STRIP */}
      <section className="relative border-y border-white/5 bg-black py-20 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="font-jp font-black text-[14rem] md:text-[22rem] text-white/[0.025] leading-none select-none">
            規律
          </div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="font-mono text-[10px] tracking-[0.5em] text-[#FF3333] mb-5">
            FORGE-AI · v1.0
          </div>
          <h2 className="font-heading uppercase text-4xl md:text-6xl leading-[0.95]">
            The coach that <br />
            <span className="text-stroke-red grunge-text">never sleeps.</span>
          </h2>
          <p className="font-sub text-white/60 mt-6 max-w-2xl mx-auto text-lg">
            Answer 10 questions. FORGE-AI builds your weekly split, calculates your macros, writes
            your meals, and adapts as you progress. Chat with it 24/7 to swap exercises, drop
            calories, or push a deload week.
          </p>
          <div className="mt-10">
            <Link
              to="/pricing"
              data-testid="forge-ai-cta"
              className="inline-flex items-center gap-3 px-10 py-4 border border-[#FF3333] bg-black text-[#FF3333] font-heading uppercase tracking-[0.3em] text-sm hover:bg-[#FF3333] hover:text-black transition-colors"
            >
              Join Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WAIFU COLLECTION TEASE */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="waifu-tease">
        <div className="grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-[#FF3333]" />
              <span className="font-mono text-[10px] tracking-[0.4em] text-[#FF3333]">浪人 · WAIFU COLLECTION</span>
            </div>
            <h2 className="font-heading uppercase text-5xl md:text-6xl leading-[0.9]">
              Streetwear, <br />
              <span className="text-stroke-red grunge-text">in the forge.</span>
            </h2>
            <p className="font-sub text-white/60 mt-6 max-w-md leading-relaxed">
              Anime-inspired tees and rashguards built for athletes who train in shadows. Sakura
              Slayer, Kunoichi, and the Ronin lineup &mdash; dropping soon for members first.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/coming-soon"
                data-testid="waifu-tease-cta"
                className="inline-flex items-center gap-3 px-8 py-3 border border-white/30 text-white/80 font-heading uppercase tracking-[0.3em] text-xs hover:border-[#FF3333] hover:text-[#FF3333] transition-colors"
              >
                Get First Dibs <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <div className="inline-flex items-center px-4 py-3 font-mono text-[10px] tracking-[0.3em] text-white/50 border border-white/10">
                COMING SOON
              </div>
            </div>
          </div>

          {/* Text-only drop cards (no product photos yet) */}
          <div className="md:col-span-6 grid grid-cols-2 gap-4">
            {[
              { kanji: "浪人", name: "Ronin Tee", tag: "Heavyweight Oversized" },
              { kanji: "桜", name: "Sakura Slayer", tag: "Crimson Onryō" },
              { kanji: "くのいち", name: "Kunoichi", tag: "Compression Rashguard" },
              { kanji: "?", name: "Drop 04", tag: "TBA — Member First Look" },
            ].map((d, i) => (
              <div
                key={d.name}
                data-testid={`waifu-drop-${i}`}
                className={`relative aspect-[3/4] border border-white/10 bg-[#0a0a0a] p-6 flex flex-col justify-between overflow-hidden group hover:border-[#FF3333]/50 transition-colors ${i % 2 === 1 ? "md:mt-10" : ""}`}
              >
                <div className="absolute -right-4 -bottom-6 font-jp font-black text-[7rem] leading-none text-white/[0.04] select-none group-hover:text-[#FF3333]/[0.10] transition-colors">
                  {d.kanji}
                </div>
                <div className="relative">
                  <div className="font-mono text-[9px] tracking-[0.3em] text-[#FF3333]">{d.kanji}</div>
                  <div className="font-mono text-[9px] tracking-[0.3em] text-white/30 mt-3">DROP {String(i + 1).padStart(2, "0")}</div>
                </div>
                <div className="relative">
                  <div className="font-heading uppercase text-xl leading-tight">{d.name}</div>
                  <div className="font-mono text-[9px] tracking-widest text-white/40 mt-2">{d.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 text-center">
          <div className="font-mono text-[10px] tracking-[0.5em] text-[#FF3333] mb-6">
            MEMBERSHIP <span className="text-white/30">•</span> ONE PRICE <span className="text-white/30">•</span> EVERYTHING UNLOCKED
          </div>
          <h2 className="font-heading uppercase text-5xl md:text-7xl leading-[0.9]">
            Stronger body. <br />
            <span className="text-stroke-red grunge-text">Stronger discipline.</span>
          </h2>
          <p className="font-sub text-white/60 mt-6 max-w-xl mx-auto text-lg">
            $9.99/month. Cancel anytime. Join the forge today.
          </p>
          <div className="mt-10">
            <Link
              to="/pricing"
              data-testid="final-cta-see-plans"
              className="inline-flex items-center gap-3 px-12 py-5 border border-[#FF3333] bg-black text-[#FF3333] font-heading uppercase tracking-[0.3em] text-sm hover:bg-[#FF3333] hover:text-black transition-colors"
              style={{ boxShadow: "0 0 30px rgba(255,51,51,0.2)" }}
            >
              Join Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
